import { Injectable, computed, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { cleanTracks, getUserMedia } from 'app/utils/media/stream';

import { BrowserService } from './browser.service';
import { DeviceService } from './device/device.service';
import { MultimediaUserService } from './multimedia-user.service';

const getVideoPermission = async () => {
  // @ts-expect-error Camera permission is non-standard
  return await navigator.permissions.query({ name: 'camera' });
};

const getAudioPermission = async () => {
  // @ts-expect-error Microphone permission is non-standard
  return await navigator.permissions.query({ name: 'microphone' });
};

enum PermissionStateType {
  GRANTED = 'granted',
  DENIED = 'denied',
  PROMPT = 'prompt',
}

const isGranted = (permissionState: PermissionState): boolean => {
  return permissionState === PermissionStateType.GRANTED;
};

@Injectable({
  providedIn: 'root',
})
export class PermissionsService extends Loggable {
  private deviceService = inject(DeviceService);
  private multimediaUserService = inject(MultimediaUserService);
  private browserService = inject(BrowserService);
  private store = inject<Store<AppState>>(Store);

  public didUpdatePermission = new Subject<DevicePermissionStatus>();

  private permission?: DevicePermissionStatus;
  private audioPermissionStream: MediaStream | null = null;
  private cameraPermissionStream: MediaStream | null = null;
  private isTestUser = false;

  public readonly videoPermission = signal<PermissionStatus | undefined>(undefined);
  public readonly audioPermission = signal<PermissionStatus | undefined>(undefined);
  public readonly hasCheckedDevices = signal(false);

  public constructor() {
    super();

    this.setDisplayName('PermissionsService');
    this.deviceService.audioStream$.subscribe((stream: MediaStream | undefined) => this.audioStreamChange(stream));
    this.store.select(coreSelectors.isTestUser).subscribe((isTestUser: boolean) => {
      this.isTestUser = isTestUser;
    });
  }

  private async createAudioPermissionStream() {
    this.audioPermissionStream = await getUserMedia({ audio: true }, this);
  }

  private async audioStreamChange(_newStream: MediaStream | undefined) {
    // When the mic changes, if we have a stream held for permission reasons, stop the tracks for that stream and remove
    // it. We no longer need to hold the mic for permissions (in firefox, specifically), and firefox does not allow
    // multiple mics to be held at once.
    if (this.audioPermissionStream) {
      await cleanTracks(this.audioPermissionStream, this);
      this.audioPermissionStream = null;
    }
  }

  public readonly isVideoPermissionDenied = computed(() => {
    return this.videoPermission()?.state === PermissionStateType.DENIED;
  });

  public readonly isAudioPermissionDenied = computed(() => {
    return this.audioPermission()?.state === PermissionStateType.DENIED;
  });

  public async getPermissionStatus(): Promise<DevicePermissionStatus> {
    this.info('Getting Permission Status');
    this.permission = new DevicePermissionStatus();

    if (this.isTestUser) {
      this.permission.setGrantedPermissions({ video: true, audio: true });
      return this.permission;
    }

    //Check if the browser has camera and mic access at a system level
    const systemVideoAccess = await this.getSystemAccessStatus({ video: true, audio: false });
    const systemAudioAccess = await this.getSystemAccessStatus({ video: false, audio: true });

    //if both system level access are denied return the current permission state
    if (!systemVideoAccess && !systemVideoAccess) {
      this.permission.setGrantedPermissions({
        video: systemVideoAccess,
        audio: systemAudioAccess,
      });
      return this.permission;
    }

    //Used to compare browser permissions status

    if (!systemVideoAccess) {
      if (this.browserService.isSafari || this.browserService.isFirefox) {
        try {
          await this.createAudioPermissionStream();
          this.permission.setGrantedPermissions({
            video: false,
            audio: true,
          });
          return this.permission;
        } catch {
          this.permission.setGrantedPermissions({
            video: false,
            audio: false,
          });
          return this.permission;
        }
      }

      //if only camera is blocked at system level then only check audio permission at browser level
      const audioPermission = await getAudioPermission();
      const audioGranted = isGranted(audioPermission.state);

      this.permission.setGrantedPermissions({
        video: false,
        audio: audioGranted,
      });
      this.setPermissionListener(audioPermission, 'audio');
      return this.permission;
    }

    if (!systemAudioAccess) {
      if (this.browserService.isSafari || this.browserService.isFirefox) {
        try {
          this.cameraPermissionStream = await getUserMedia({ video: true }, this);
          await cleanTracks(this.cameraPermissionStream, this);
          this.permission.setGrantedPermissions({
            video: true,
            audio: false,
          });
          return this.permission;
        } catch {
          this.permission.setGrantedPermissions({
            video: false,
            audio: false,
          });
          return this.permission;
        }
      }

      //if only mic is blocked at system level only then check camera permission at browser level
      const videoPermission = await getVideoPermission();
      const videoGranted = isGranted(videoPermission.state);
      this.setPermissionListener(videoPermission, 'video');
      this.permission.setGrantedPermissions({
        video: videoGranted,
        audio: false,
      });
      return this.permission;
    }

    if (this.browserService.isSafari || this.browserService.isFirefox) {
      let video = false;
      let audio = false;
      try {
        this.cameraPermissionStream = await getUserMedia({ video: true }, this);
        await cleanTracks(this.cameraPermissionStream, this);
        video = true;
      } catch {
        // no-op
      }

      try {
        this.audioPermissionStream = await getUserMedia({ audio: true }, this);
        await cleanTracks(this.audioPermissionStream, this);
        audio = true;
      } catch {
        // no-op
      }

      this.permission.setGrantedPermissions({ video, audio });
      return this.permission;
    }
    // if both mic and camera have system level access then check browser permissions
    const videoPermission = await getVideoPermission();
    const audioPermission = await getAudioPermission();

    const videoGranted = isGranted(videoPermission.state);
    const audioGranted = isGranted(audioPermission.state);

    this.videoPermission.set(videoPermission);
    this.audioPermission.set(audioPermission);

    this.setPermissionListener(videoPermission, 'video');
    this.setPermissionListener(audioPermission, 'audio');
    this.permission.setGrantedPermissions({ video: videoGranted, audio: audioGranted });
    this.hasCheckedDevices.set(true);
    return this.permission;
  }

  private setPermissionListener(permissions: PermissionStatus, type: 'audio' | 'video'): void {
    permissions.onchange = (event) => {
      const { state } = event.target as PermissionStatus;
      const granted = isGranted(state);
      this.info(`Permission Updated for type ${type} with state ${state}`);

      if (type === 'video') {
        this.permission?.setGrantedVideo(granted);
        if (granted) {
          // Disabling and enabling video is enough to unstuck it after permissions are changed
          this.multimediaUserService.disableVideo();
          this.multimediaUserService.enableVideo();
        } else {
          this.multimediaUserService.disableVideo();
        }
      }

      if (type === 'audio') {
        this.permission?.setGrantedAudio(granted);
        if (granted) {
          // To unstuck audio after permissions changed, it's necessary to clear the tracks and reselect a device
          this.deviceService
            .reInitializeAudio()
            .then(() => {
              this.multimediaUserService.enableMic();
              this.info('Permissions Changed - Microphone reselected successfully');
            })
            .catch((e) => {
              this.error('Permissions Changed - Error resetting microphone after permissions changed ', e);
            });
        } else {
          this.multimediaUserService.disableMic();
        }
      }

      if (this.permission) {
        this.didUpdatePermission.next(this.permission);
      }
    };
  }

  private async getSystemAccessStatus({ audio, video }: { video: boolean; audio: boolean }): Promise<boolean> {
    try {
      const permissionsStream = await getUserMedia({ video, audio }, this);
      await cleanTracks(permissionsStream, this);
      return true;
    } catch (error) {
      return !(error as object).toString().toLowerCase().includes('system');
    }
  }

  public async cleanAllStreams(): Promise<void> {
    if (this.audioPermissionStream) {
      await cleanTracks(this.audioPermissionStream, this);
      this.audioPermissionStream = null;
    }

    if (this.cameraPermissionStream) {
      await cleanTracks(this.cameraPermissionStream, this);
      this.cameraPermissionStream = null;
    }
  }

  public requestVideoPermission(): void {
    getVideoPermission().catch((e) => {
      this.warn(e);
    });
  }

  public requestAudioPermission(): void {
    getAudioPermission().catch((e) => {
      this.warn(e);
    });
  }
}

export default class DevicePermissionStatus {
  public readonly videoGranted = signal(false);
  public readonly audioGranted = signal(false);

  public setGrantedPermissions({ audio, video }: { video: boolean; audio: boolean }): void {
    this.videoGranted.set(video);
    this.audioGranted.set(audio);
  }

  public setGrantedVideo(video: boolean): void {
    this.videoGranted.set(video);
  }

  public setGrantedAudio(audio: boolean): void {
    this.audioGranted.set(audio);
  }
}
